import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BookmarkSimple, Plus, Trash, ArrowRight, ArrowSquareOut, ArrowsClockwise, MagicWand } from "@phosphor-icons/react";
import { ROICell, ScorePill } from "@/components/opportunities/ScorePill";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { amazonUrl, bricklinkUrl } from "@/lib/links";

const money = (n) => (n == null ? "--" : `$${Number(n).toFixed(2)}`);

export default function Watchlists() {
  const [lists, setLists] = useState([]);
  const [active, setActive] = useState(null);
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ done: 0, total: 0 });

  const load = async () => {
    const l = await api.listWatchlists();
    setLists(l);
    if (!active && l.length > 0) setActive(l[0]);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active) api.watchlistOpps(active.id).then(d => setRows(d.rows)).catch(() => setRows([]));
  }, [active]);

  const createList = async () => {
    if (!newName.trim()) return;
    const w = await api.createWatchlist({ name: newName });
    setNewName("");
    setCreateOpen(false);
    await load();
    setActive(w);
    toast.success(`Created ${w.name}`);
  };

  const deleteList = async (id) => {
    await api.deleteWatchlist(id);
    toast.success("Deleted");
    setActive(null);
    await load();
  };

  const removeItem = async (setNo) => {
    await api.removeFromWatchlist(active.id, setNo);
    const d = await api.watchlistOpps(active.id);
    setRows(d.rows);
    toast.success("Removed");
  };

  const refreshAll = async () => {
    if (!active || !active.set_numbers?.length) {
      toast.error("Watchlist is empty");
      return;
    }
    const total = active.set_numbers.length;
    setRefreshing(true);
    setRefreshProgress({ done: 0, total });
    let live = 0, partial = 0, mock = 0;
    for (let i = 0; i < total; i++) {
      const sn = active.set_numbers[i];
      try {
        const d = await api.liveLookup(sn);
        const ds = d.opportunity?.data_source;
        if (ds === "live") live++;
        else if (ds === "partial") partial++;
        else mock++;
      } catch {
        mock++;
      }
      setRefreshProgress({ done: i + 1, total });
    }
    const d = await api.watchlistOpps(active.id);
    setRows(d.rows);
    setRefreshing(false);
    toast.success(`Refreshed ${total} sets · ${live} live, ${partial} partial, ${mock} demo`);
  };

  const asinOnlyScan = async () => {
    if (!active || !active.set_numbers?.length) {
      toast.error("Watchlist is empty");
      return;
    }
    try {
      const j = await api.asinResolverRun({
        set_numbers: active.set_numbers,
        limit: active.set_numbers.length,
        skip_attempted: false,   // watchlists are curated; re-check even attempted
        retry_failed: true,
        continuous: true,        // auto-sleep on token refill
        fetch_bricklink: false,  // ASIN-only — skip BL
      });
      toast.info(`ASIN resolver started for ${active.set_numbers.length} watchlist set(s) — check Scan Engine for progress`);
      // Watchlists page doesn't own a job-status widget; the Scans page shows it.
      return j;
    } catch (e) {
      toast.error(e.response?.data?.detail || "ASIN scan failed to start");
    }
  };

  return (
    <div className="p-6 flex flex-col md:flex-row gap-4 h-[calc(100vh-86px)]">
      {/* Lists sidebar */}
      <div className="md:w-64 border border-[#E4E4E0] rounded-sm">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#E4E4E0]">
          <div className="flex items-center gap-2 font-head text-xs uppercase tracking-wider">
            <BookmarkSimple size={14} weight="bold" className="text-[#E3000B]" /> WATCHLISTS
          </div>
          <button onClick={() => setCreateOpen(true)} className="text-[#E3000B] hover:text-[#E6B800]" data-testid="create-watchlist-btn"><Plus size={14} weight="bold" /></button>
        </div>
        <div className="p-1">
          {lists.length === 0 && <div className="p-4 text-xs font-mono text-gray-500" data-testid="watchlists-empty">No watchlists yet.</div>}
          {lists.map(w => (
            <button key={w.id} onClick={() => setActive(w)}
              data-testid={`wl-${w.id}`}
              className={`w-full flex justify-between items-center px-3 py-2 text-left text-xs font-mono rounded-sm ${active?.id === w.id ? "bg-[#E3000B]/10 text-[#E3000B]" : "text-gray-600 hover:bg-[#F6F6F3]"}`}>
              <span>{w.name}</span>
              <span className="text-gray-400">{w.set_numbers?.length || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Active list */}
      <div className="flex-1 overflow-auto border border-[#E4E4E0] rounded-sm">
        {!active && <div className="p-8 text-xs font-mono text-gray-500">Select a watchlist.</div>}
        {active && (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E4E0]">
              <h2 className="font-head text-lg font-black uppercase tracking-tighter">{active.name}</h2>
              <div className="flex items-center gap-2">
                {refreshing && (
                  <span className="text-[10px] font-mono text-[#E3000B]" data-testid="wl-refresh-progress">
                    {refreshProgress.done}/{refreshProgress.total}
                  </span>
                )}
                <Button onClick={refreshAll} disabled={refreshing || !active.set_numbers?.length}
                  className="h-8 rounded-sm bg-[#E3000B] text-white hover:bg-[#E6B800] font-bold uppercase tracking-wider text-xs disabled:opacity-50"
                  data-testid="wl-refresh-all">
                  <ArrowsClockwise size={12} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
                  {refreshing ? "REFRESHING" : "REFRESH ALL"}
                </Button>
                <Button onClick={asinOnlyScan} disabled={refreshing || !active.set_numbers?.length}
                  title="Resolve missing Amazon ASINs for this watchlist. Skips price fetch — cheap on tokens. Runs in background; watch progress on the Scan Engine page."
                  className="h-8 rounded-sm bg-transparent border border-[#D97706] text-[#D97706] hover:bg-[#D97706]/10 font-bold uppercase tracking-wider text-xs disabled:opacity-50"
                  data-testid="wl-asin-only">
                  <MagicWand size={12} weight="bold" className="mr-1" />
                  ASIN ONLY
                </Button>
                <Button onClick={() => deleteList(active.id)} variant="outline" className="h-8 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-xs font-mono hover:border-[#FF3333] hover:text-[#FF3333]" data-testid="delete-watchlist-btn">
                  <Trash size={12} className="mr-1" /> DELETE
                </Button>
              </div>
            </div>

            <table className="w-full text-xs font-mono">
              <thead className="bg-[#F6F6F3] border-b border-[#E4E4E0]">
                <tr>
                  <Th>SET</Th>
                  <Th>NAME</Th>
                  <Th>AMZ</Th>
                  <Th>BL</Th>
                  <Th>ROI</Th>
                  <Th>NET</Th>
                  <Th>SCORE</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-gray-500" data-testid="watchlist-rows-empty">No sets in this watchlist. Add from any set detail page.</td></tr>
                )}
                {rows.map(r => (
                  <tr key={r.set_number} className="border-b border-[#F6F6F3] hover:bg-[#F6F6F3]" data-testid={`wl-row-${r.set_number}`}>
                    <td className="px-3 py-2 text-[#E3000B]">{r.set_number}</td>
                    <td className="px-3 py-2 text-gray-900 truncate max-w-[220px]">{r.name}</td>
                    <td className="px-3 py-2 text-right">{money(r.amazon?.buy_box)}</td>
                    <td className="px-3 py-2 text-right">{money(r.bricklink?.lowest_sealed_us)}</td>
                    <td className="px-3 py-2 text-right"><ROICell value={r.roi_pct} /></td>
                    <td className="px-3 py-2 text-right">{money(r.net_profit)}</td>
                    <td className="px-3 py-2 text-center"><ScorePill score={r.opportunity_score} /></td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end items-center gap-1">
                        <a href={amazonUrl(r.asin, r.set_number, r.asin_verified)} target="_blank" rel="noopener noreferrer"
                           onClick={(e) => e.stopPropagation()}
                           title={r.asin_verified ? "Open verified Amazon listing" : "Search on Amazon"}
                           data-testid={`wl-amz-${r.set_number}`}
                           className="inline-flex items-center gap-0.5 px-1.5 h-5 border border-[#E4E4E0] rounded-sm text-[9px] font-bold text-[#D97706] hover:border-[#D97706] hover:bg-[#D97706]/10">
                          AMZ <ArrowSquareOut size={9} />
                        </a>
                        <a href={bricklinkUrl(r.set_number)} target="_blank" rel="noopener noreferrer"
                           onClick={(e) => e.stopPropagation()}
                           title="Open on BrickLink"
                           data-testid={`wl-bl-${r.set_number}`}
                           className="inline-flex items-center gap-0.5 px-1.5 h-5 border border-[#E4E4E0] rounded-sm text-[9px] font-bold text-[#0055BF] hover:border-[#0055BF] hover:bg-[#0055BF]/10">
                          BL <ArrowSquareOut size={9} />
                        </a>
                        <Link to={`/set/${r.set_number}`} className="text-gray-600 hover:text-[#E3000B] ml-1" data-testid={`wl-open-${r.set_number}`}><ArrowRight size={14} /></Link>
                        <button onClick={() => removeItem(r.set_number)} className="text-gray-600 hover:text-[#FF3333]" data-testid={`wl-remove-${r.set_number}`}><Trash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E4E4E0] rounded-sm text-gray-900">
          <DialogHeader><DialogTitle className="font-head uppercase">New Watchlist</DialogTitle></DialogHeader>
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Q4 Star Wars Priority" className="bg-[#F6F6F3] border-[#E4E4E0] font-mono h-9" data-testid="new-watchlist-name" />
          <DialogFooter>
            <Button onClick={createList} className="bg-[#E3000B] text-white hover:bg-[#E6B800] font-bold uppercase rounded-sm" data-testid="new-watchlist-confirm">CREATE</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-gray-500">{children}</th>;
}
